import { useEffect, useRef, useState } from "react";
import s from "./MomentsGallery.module.scss";
import { getSectionText } from "../data/sectionTexts";
import { useLanguage } from "../contexts/LanguageContext";
import Title from "../Title/Title";
import { img } from "../utils/cloudinaryImage";
import { momentsGalleryImages } from "../data/momentsGalleryImages";
import ImageLightbox, {
  ImageZoomButton,
} from "../components/ImageLightbox/ImageLightbox";

const GALLERY_IMAGE_PRESET = {
  height: 340,
  crop: "fit",
  gravity: "center",
  quality: "auto",
};

const EMPTY_IMAGE =
  'data:image/svg+xml;charset=utf-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="36" height="23"%3E%3Crect width="36" height="23" fill="transparent"/%3E%3C/svg%3E';

const getScrollMetrics = (slider) => {
  if (!slider) {
    return {
      canScroll: false,
      currentPage: 0,
      pageCount: 1,
    };
  }

  const scrollable = slider.scrollWidth > slider.clientWidth + 2;

  if (!scrollable) {
    return {
      canScroll: false,
      currentPage: 0,
      pageCount: 1,
    };
  }

  const pageCount = Math.max(
    1,
    Math.ceil(slider.scrollWidth / slider.clientWidth),
  );
  const maxScroll = slider.scrollWidth - slider.clientWidth;
  const ratio = maxScroll > 0 ? slider.scrollLeft / maxScroll : 0;
  const currentPage = Math.round(ratio * (pageCount - 1));

  return {
    canScroll: true,
    currentPage: Math.max(0, Math.min(currentPage, pageCount - 1)),
    pageCount,
  };
};

const ScrollDots = ({ canScroll, pageCount, currentPage }) => {
  if (!canScroll || pageCount <= 1) return null;

  return (
    <div className={s.dots} aria-hidden="true">
      {Array.from({ length: pageCount }).map((_, index) => (
        <span key={index} className={index === currentPage ? s.active : ""} />
      ))}
    </div>
  );
};

const GalleryImage = ({ src, shouldLoad }) => {
  return (
    <img
      src={shouldLoad ? img(src, GALLERY_IMAGE_PRESET) : EMPTY_IMAGE}
      alt="Miyako tour moment"
      loading="lazy"
      fetchPriority="low"
      decoding="async"
    />
  );
};

const MomentsGallery = () => {
  const { lang } = useLanguage();
  const titleText = getSectionText(lang, "memories");
  const photoItems = momentsGalleryImages;

  const rootRef = useRef(null);
  const sliderRef = useRef(null);
  const [shouldLoadImages, setShouldLoadImages] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [canScroll, setCanScroll] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const [enlargedImage, setEnlargedImage] = useState(null);

  const updateScrollState = () => {
    const photoMetrics = getScrollMetrics(sliderRef.current);

    setCanScroll(photoMetrics.canScroll);
    setCurrentPage(photoMetrics.currentPage);
    setPageCount(photoMetrics.pageCount);
  };

  useEffect(() => {
    const target = rootRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoadImages(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "500px 0px",
        threshold: 0.01,
      },
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return undefined;

    updateScrollState();
    slider.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);

    return () => {
      slider.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, []);

  return (
    <section className={s.root} ref={rootRef}>
      <Title kicker={titleText.kicker} title={titleText.title} />

      <div className={s.mediaBlock}>
        <div className={`${s.slider} ${s.photoSlider}`} ref={sliderRef}>
          <div className={s.track}>
            {photoItems.map((image, index) => {
              return (
                <div className={s.item} key={`${image}-${index}`}>
                  <GalleryImage src={image} shouldLoad={shouldLoadImages} />
                  <ImageZoomButton
                    onClick={() => setEnlargedImage(image)}
                    ariaLabel={`Miyako tour moment ${index + 1}を大きく見る`}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <ScrollDots
          canScroll={canScroll}
          pageCount={pageCount}
          currentPage={currentPage}
        />
      </div>

      <ImageLightbox
        image={enlargedImage}
        alt="Miyako tour moment"
        onClose={() => setEnlargedImage(null)}
      />
    </section>
  );
};

export default MomentsGallery;
